import { merchstoreStorefrontClient } from "@merchstores/admin/App";
import * as storefrontQuery from "@merchstores/admin/queries/storefront-graphql";
import { IMerchStore } from "@merchstores/admin/components/MerchStore";

import { OrderType, CLOSE_DATE_PENDING, PayerType } from '@merchstores/shared/components/MerchStore';
import { IOrderItem } from "./OrderItem";

export interface ICustomAttribute {
  key: string,
  value: string
}

export interface ILineItem {
  variantId: string,
  quantity: number,
  customAttributes: Array<ICustomAttribute>
}

export interface ICheckoutFields {
  lineItems: Array<ILineItem>,
  customAttributes: Array<ICustomAttribute>
}

export function customAttr(attrName: string, attrValue: string): ICustomAttribute {
  return { key: attrName, value: attrValue };
}

export function buildLineItemAttributes(item: IOrderItem, merchstore: IMerchStore): Array<ICustomAttribute> {
  let attributes: Array<ICustomAttribute> = [];

  if (process.env.REACT_APP_COMBINED_ORDER_FAKE_CHECKOUT)
  {
    // force admin pays in combined orders
    attributes = attributes.concat(
      customAttr('order_type', OrderType.INDIVIDUAL),
      customAttr('payer_type', PayerType.ADMIN)
    );
  }
  else
  {
    attributes = attributes.concat(
      customAttr('payer_type', merchstore.payerType || ''),
      customAttr('order_type', OrderType.COMBINED)
    );
  }

  attributes = attributes.concat(
    customAttr('Decoration type', item.decorationType || ''),
    customAttr('Select decoration location', item.decorationLocation || ''),
    customAttr('Upload your logo (2MB max)', item.artwork || ''),
    customAttr('Logo Mockup Image', item.logoMockup || ''),
    customAttr('ship_to_office', String(merchstore.shipToOffice) || '')
  );

  return attributes.filter(attr => typeof attr.value !== 'undefined');
}

export function buildCheckoutFields(orderItems: Array<IOrderItem>, merchstore: IMerchStore): ICheckoutFields {
  const baseLineItemAttributes: Array<ICustomAttribute> = [];

  const lineItems = orderItems.map(item => {
    const lineItemAttributes = baseLineItemAttributes.concat(
      buildLineItemAttributes(item, merchstore)
    );

    const encodedVariantId = Buffer.from(item.variantId).toString('base64');

    return {
      variantId: encodedVariantId,
      quantity: item.quantity,
      customAttributes: lineItemAttributes
    }
  });

  const cartAttributes: Array<ICustomAttribute> = [
    customAttr('netsuite_sync', 'false'),
    customAttr('subdomain', merchstore.subdomain),
    customAttr('merchstore_code', merchstore.storeCode + ''),
    customAttr('payer_type', merchstore.payerType + ''),
    customAttr('order_type', OrderType.COMBINED),
    customAttr('ship_to_office', merchstore.shipToOffice + ''),
    customAttr('close_date', CLOSE_DATE_PENDING),
    customAttr('merchstore_logo', merchstore.storeLogo + ''),

  ].filter(attr => typeof attr.value !== 'undefined');

  const checkoutFields: ICheckoutFields = {
    lineItems,
    customAttributes: cartAttributes
  }

  return checkoutFields;
}

interface IErrorInvalidItem {
  code: string,
  variantId: string
}

export async function extractCheckoutErrors(
  checkoutErrors: Array<{ code: string }>,
  checkoutFields: ICheckoutFields
): Promise<Array<IErrorInvalidItem>> {
  const errors: Array<IErrorInvalidItem> = [];

  const invalidErrors = checkoutErrors.filter((errorItem) => errorItem.code === 'INVALID');

  if (!invalidErrors.length) {
    return errors;
  }

  const invalidItemErrorDetails = await Promise.all(checkoutFields.lineItems.map(lineItem => {
    const errorCode = 'INVALID';
    const productVariantQuery = storefrontQuery.getProductVariantByIdQuery(lineItem.variantId);

    const checkErrorPromise = new Promise((resolve) => {
      merchstoreStorefrontClient.send(productVariantQuery).then(({ data }: { data: { node: unknown } }) => {
        if (!data.node) {
          return resolve({ code: errorCode, variantId: lineItem.variantId });
        }
        resolve(null);
      });
    });

    return checkErrorPromise as Promise<IErrorInvalidItem | null>;
  }));

  invalidItemErrorDetails.filter(Boolean).forEach((errorItem) => {
    errorItem = errorItem as IErrorInvalidItem;
    errors.push({ code: errorItem.code, variantId: errorItem.variantId });
  });

  return errors;
}

export interface ICheckoutStatus {
  checkoutUrl: string,
  errors: Array<IErrorInvalidItem>
}

export async function requestCheckoutUrl(checkoutFields: ICheckoutFields): Promise<ICheckoutStatus> {
  const checkoutCreateMutation = storefrontQuery.getCheckoutCreateMutation(checkoutFields);

  const createResponse = await merchstoreStorefrontClient.send(checkoutCreateMutation);

  if (createResponse.errors) {
    throw new Error(JSON.stringify(createResponse.errors));
  }

  const createData = createResponse.data.checkoutCreate;

  const createErrors = createData.checkoutUserErrors;

  const errors = await extractCheckoutErrors(createErrors, checkoutFields);

  const checkoutData = createData.checkout;
  const checkoutUrl = checkoutData ? checkoutData.webUrl : '';

  return {
    checkoutUrl: checkoutUrl,
    errors: errors
  }
}
